import { useEffect, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { getContentByIdOptions, getContentFeedInfiniteOptions, TContentFeedFilters } from '@/utils/queryOptions';

// Published content the current viewer can see (org visibility + domains), filtered
// server-side by domains / subdomains / visibility / author / content type. Paginated
// via an infinite query so the grid grows on scroll instead of one large request.
const useContentFeed = (filters: TContentFeedFilters = {}) => {
  const queryClient = useQueryClient();

  const { data, isLoading, isError, isPlaceholderData, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery(getContentFeedInfiniteOptions(filters));

  const content = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data?.pages]);

  // Seed the per-item detail cache from the list. List items are the full ContentDto
  // (including `details`), so the detail page can render instantly with no extra fetch.
  // Guarded on `details` so this stays correct if the list endpoint ever trims the body.
  useEffect(() => {
    content.forEach((item) => {
      if (item.details) queryClient.setQueryData(getContentByIdOptions(item.id).queryKey, item);
    });
  }, [content, queryClient]);

  // `isPlaceholderData` is true while a new filter combination loads but the previous
  // grid is still shown (keepPreviousData) — the signal for a "filtering" indicator.
  return { content, isLoading, isError, isFiltering: isPlaceholderData, refetch, fetchNextPage, hasNextPage, isFetchingNextPage };
};

export default useContentFeed;
